import React, { useState, useCallback } from 'react';
import { BarChart3, Users, User, ChevronRight, Loader, AlertCircle } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { btn } from '../../utils/buttonStyles';
import { apiCall } from '../../services/apiUtil';

/**
 * StatsTab — Squad statistics with lazy-loaded sections.
 *
 * Left nav: Attendance | Individual Performance | Team Performance
 * Each section loads data only when the user clicks "Show".
 */

const SECTIONS = [
  { id: 'attendance',   label: 'Attendance',           icon: Users },
  { id: 'individual',   label: 'Individual Performance', icon: User },
  { id: 'team',         label: 'Team Performance',      icon: BarChart3 },
];

const StatsTab = ({ voProvider }) => {
  const { theme } = useTheme();
  const [activeSection, setActiveSection] = useState('attendance');

  // Per-section state: null = not loaded, 'loading' = fetching, data object = loaded
  const [sectionData, setSectionData] = useState({ attendance: null, individual: null, team: null });
  const [sectionError, setSectionError] = useState({ attendance: null, individual: null, team: null });

  const group   = voProvider?.getGroup();
  const groupId = voProvider?.vo?.groupId;
  const members = voProvider?.getMembers() || [];

  if (!voProvider) {
    return (
      <div className={`${theme.card} rounded-lg p-8 text-center`}>
        <Loader className="w-6 h-6 animate-spin mx-auto text-blue-500" />
      </div>
    );
  }

  // ─── Loaders ────────────────────────────────────────────────────────────────

  const loadAttendance = useCallback(async () => {
    setSectionData(prev => ({ ...prev, attendance: 'loading' }));
    setSectionError(prev => ({ ...prev, attendance: null }));
    try {
      const result = await apiCall(`/groups/${groupId}/stats/attendance`);

      // Merge API counts with member name info from voProvider
      const memberMap = {};
      members.forEach(m => {
        memberMap[m.userId] = {
          userId:      m.userId,
          name:        `${m.firstName} ${m.lastName}`.trim(),
          available:   0,
          unavailable: 0,
          maybe:       0,
          total:       result.totalEvents,
        };
      });

      (result.members || []).forEach(r => {
        if (memberMap[r.userId]) {
          memberMap[r.userId].available   = r.available;
          memberMap[r.userId].unavailable = r.unavailable;
          memberMap[r.userId].maybe       = r.maybe;
        }
      });

      setSectionData(prev => ({
        ...prev,
        attendance: {
          totalEvents: result.totalEvents,
          members: Object.values(memberMap).sort((a, b) => b.available - a.available),
        }
      }));
    } catch (err) {
      setSectionError(prev => ({ ...prev, attendance: 'Failed to load attendance data.' }));
      setSectionData(prev => ({ ...prev, attendance: null }));
    }
  }, [groupId, members]);

  const loadIndividual = useCallback(async () => {
    setSectionData(prev => ({ ...prev, individual: 'loading' }));
    setSectionError(prev => ({ ...prev, individual: null }));
    try {
      const userIds = members.map(m => m.userId).filter(Boolean);
      if (userIds.length === 0) {
        setSectionData(prev => ({ ...prev, individual: [] }));
        return;
      }

      // Single API call → single DB query for all members, scoped to this group
      const statsRows = await apiCall(`/users/stats/bulk?userIds=${userIds.join(',')}&groupId=${groupId}`);
      const statsMap = {};
      (statsRows || []).forEach(s => { statsMap[s.userId] = s; });

      const results = members.map(m => {
        const s = statsMap[m.userId] || {};
        return {
          userId:        m.userId,
          name:          `${m.firstName} ${m.lastName}`.trim(),
          totalMatches:  s.totalMatches  || 0,
          totalWins:     s.totalWins     || 0,
          totalLosses:   s.totalLosses   || 0,
          overallWinRate:s.overallWinRate || 0,
        };
      });

      setSectionData(prev => ({
        ...prev,
        individual: results.sort((a, b) => (b.overallWinRate || 0) - (a.overallWinRate || 0)),
      }));
    } catch (err) {
      setSectionError(prev => ({ ...prev, individual: 'Failed to load individual stats.' }));
      setSectionData(prev => ({ ...prev, individual: null }));
    }
  }, [members, groupId]);

  const loadTeam = useCallback(async () => {
    setSectionData(prev => ({ ...prev, team: 'loading' }));
    setSectionError(prev => ({ ...prev, team: null }));
    try {
      const teamStats = await apiCall(`/groups/${groupId}/stats/teams`);
      setSectionData(prev => ({ ...prev, team: teamStats || [] }));
    } catch (err) {
      console.error('[StatsTab] loadTeam error:', err);
      setSectionError(prev => ({ ...prev, team: 'Failed to load team stats.' }));
      setSectionData(prev => ({ ...prev, team: null }));
    }
  }, [groupId]);

  const loaders = { attendance: loadAttendance, individual: loadIndividual, team: loadTeam };

  // ─── Section renderers ───────────────────────────────────────────────────────

  const renderShowButton = (sectionId) => (
    <div className="flex flex-col items-center justify-center py-16 gap-4">
      <BarChart3 className={`w-10 h-10 ${theme.textMuted}`} />
      <p className={`text-sm ${theme.textMuted}`}>Click to load statistics</p>
      <button
        onClick={() => loaders[sectionId]()}
        className={`${btn.base} ${btn.md} ${theme.buttonPrimary}`}
      >
        Show
      </button>
    </div>
  );

  const renderLoading = () => (
    <div className="flex items-center justify-center py-16">
      <Loader className="w-6 h-6 animate-spin text-blue-500" />
    </div>
  );

  const renderError = (msg, sectionId) => (
    <div className="flex flex-col items-center gap-3 py-12">
      <AlertCircle className="w-6 h-6 text-red-500" />
      <p className={`text-sm ${theme.textMuted}`}>{msg}</p>
      <button onClick={() => loaders[sectionId]()} className="text-sm text-blue-500 underline">Retry</button>
    </div>
  );

  const renderAttendance = (data) => (
    <div className="space-y-4">
      <p className={`text-xs ${theme.textMuted}`}>Based on last {data.totalEvents} events</p>
      <div className={`divide-y ${theme.border}`}>
        {data.members.length === 0 && (
          <p className={`text-sm ${theme.textMuted} py-4`}>No member data available.</p>
        )}
        {data.members.map(m => {
          const rate = m.total > 0 ? Math.round((m.available / m.total) * 100) : 0;
          return (
            <div key={m.userId} className="py-3 flex items-center justify-between gap-4">
              <span className={`text-sm font-medium ${theme.text} truncate`}>{m.name}</span>
              <div className="flex items-center gap-4 flex-shrink-0">
                <span className="text-xs text-green-500">{m.available} ✓</span>
                <span className="text-xs text-red-400">{m.unavailable} ✗</span>
                <span className="text-xs text-yellow-400">{m.maybe} ~</span>
                <div className="w-20 bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                  <div className="bg-green-500 h-1.5 rounded-full" style={{ width: `${rate}%` }} />
                </div>
                <span className={`text-xs font-semibold w-10 text-right ${theme.text}`}>{rate}%</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderIndividual = (data) => (
    <div className={`divide-y ${theme.border}`}>
      {data.length === 0 && (
        <p className={`text-sm ${theme.textMuted} py-4`}>No stats available.</p>
      )}
      {data.map(m => (
        <div key={m.userId} className="py-3 flex items-center justify-between gap-4">
          <span className={`text-sm font-medium ${theme.text} truncate`}>{m.name}</span>
          <div className="flex items-center gap-3 flex-shrink-0 text-xs">
            <span className={`${theme.textMuted}`}>{m.totalMatches || 0} played</span>
            <span className="text-green-500">{m.totalWins || 0}W</span>
            <span className="text-red-400">{m.totalLosses || 0}L</span>
            <span className={`font-semibold w-12 text-right ${
              (m.overallWinRate || 0) >= 50 ? 'text-green-500' : 'text-red-400'
            }`}>
              {Math.round(m.overallWinRate || 0)}%
            </span>
          </div>
        </div>
      ))}
    </div>
  );

  const renderTeam = (data) => {
    if (data.length === 0) {
      return (
        <p className={`text-sm ${theme.textMuted} py-4`}>
          No teams found for this squad. Create teams in the Teams tab and play games to see stats here.
        </p>
      );
    }

    return (
      <div className="space-y-3">
        {data.map(team => {
          const winRate = team.gamesPlayed > 0 ? Math.round((team.wins / team.gamesPlayed) * 100) : 0;
          return (
            <div key={team.teamId} className={`${theme.backgroundSecondary || theme.card} border ${theme.border} rounded-xl p-4`}>
              <div className="flex items-center justify-between mb-3">
                <span className={`font-semibold ${theme.text} truncate`}>{team.teamName}</span>
                <span className={`text-sm font-bold ${winRate >= 50 ? 'text-green-500' : team.gamesPlayed === 0 ? theme.textMuted : 'text-red-400'}`}>
                  {team.gamesPlayed > 0 ? `${winRate}%` : '—'}
                </span>
              </div>

              <div className="flex items-center gap-4 text-xs mb-3">
                <span className={theme.textMuted}>{team.gamesPlayed} played</span>
                <span className="text-green-500 font-medium">{team.wins}W</span>
                <span className="text-red-400 font-medium">{team.losses}L</span>
                {team.draws > 0 && <span className={`${theme.textMuted} font-medium`}>{team.draws}D</span>}
              </div>

              {/* Win rate bar */}
              {team.gamesPlayed > 0 && (
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 mb-3">
                  <div
                    className={`h-1.5 rounded-full ${winRate >= 50 ? 'bg-green-500' : 'bg-red-400'}`}
                    style={{ width: `${winRate}%` }}
                  />
                </div>
              )}

              {/* Recent form badges */}
              {team.recentResults.length > 0 && (
                <div className="flex gap-1.5">
                  {team.recentResults.map((r, i) => {
                    const color = r === 'W' ? 'bg-green-500' : r === 'L' ? 'bg-red-400' : 'bg-gray-400';
                    return (
                      <span key={i} className={`${color} text-white text-xs font-bold w-6 h-6 rounded flex items-center justify-center`}>
                        {r}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderContent = () => {
    const data = sectionData[activeSection];
    const err  = sectionError[activeSection];

    if (err) return renderError(err, activeSection);
    if (data === null) return renderShowButton(activeSection);
    if (data === 'loading') return renderLoading();

    if (activeSection === 'attendance')  return renderAttendance(data);
    if (activeSection === 'individual')  return renderIndividual(data);
    if (activeSection === 'team')        return renderTeam(data);
    return null;
  };

  // ─── Layout ──────────────────────────────────────────────────────────────────

  return (
    <div className="flex gap-0 sm:gap-6 flex-col sm:flex-row">
      {/* Left nav */}
      <div className="flex sm:flex-col gap-1 sm:w-52 flex-shrink-0 mb-4 sm:mb-0">
        {SECTIONS.map(({ id, label, icon: Icon }) => {
          const isActive = activeSection === id;
          const loaded = sectionData[id] !== null && sectionData[id] !== 'loading';
          return (
            <button
              key={id}
              onClick={() => setActiveSection(id)}
              className={`flex items-center gap-2.5 px-4 py-3 rounded-xl text-sm font-medium transition-colors text-left w-full ${
                isActive
                  ? 'bg-blue-600 text-white'
                  : `${theme.card} ${theme.text} ${theme.cardHover} border ${theme.border}`
              }`}
            >
              <Icon className="w-4 h-4 flex-shrink-0" />
              <span className="flex-1 truncate">{label}</span>
              {loaded && !isActive && (
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0" />
              )}
              {isActive && <ChevronRight className="w-3 h-3 flex-shrink-0 opacity-70" />}
            </button>
          );
        })}
      </div>

      {/* Content panel */}
      <div className={`flex-1 ${theme.card} border ${theme.border} rounded-xl p-5 min-h-[300px]`}>
        <h3 className={`font-semibold ${theme.text} mb-4 text-sm uppercase tracking-wide`}>
          {SECTIONS.find(s => s.id === activeSection)?.label}
        </h3>
        {renderContent()}
      </div>
    </div>
  );
};

export default StatsTab;
